import { z } from 'zod';
import { ALL_FUND_CODES, type FundCode } from '../constants/funds.js';
import { ALL_ROLES, type Role } from '../constants/roles.js';
import { SubHeadType } from '../constants/sub-heads.js';

/** Strict union of fund codes (PVT, PUB, REG, …). */
export const fundCodeSchema: z.ZodType<FundCode> = z.enum(
  ALL_FUND_CODES as readonly [FundCode, ...FundCode[]],
);

/** Strict union of role names (CO, DATA_ENTRY). */
export const roleSchema: z.ZodType<Role> = z.enum(ALL_ROLES as readonly [Role, ...Role[]]);

/** Sub-head type — INCOME, EXPENSE, or BOTH. */
export const subHeadTypeSchema = z.enum([
  SubHeadType.INCOME,
  SubHeadType.EXPENSE,
  SubHeadType.BOTH,
]);

/** Transaction direction. */
export const transactionDirectionSchema = z.enum(['CREDIT', 'DEBIT']);
export type TransactionDirection = z.infer<typeof transactionDirectionSchema>;

/**
 * BDT monetary amount — accepts a string or number, validates it parses as
 * a positive decimal with at most 2 decimal places, returns a normalised
 * string (canonical for `Decimal(14,2)` storage).
 *
 * The string form is preferred end-to-end to avoid IEEE-754 drift.
 */
export const bdtAmountSchema = z
  .union([z.string(), z.number()], { message: 'Amount is required' })
  .transform((v, ctx) => {
    const raw = typeof v === 'number' ? v.toString() : v.trim();
    if (raw.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Amount is required',
      });
      return z.NEVER;
    }
    if (!/^\d+(\.\d{1,2})?$/.test(raw)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Amount must be a number with at most 2 decimal places',
      });
      return z.NEVER;
    }
    // 14 total digits incl. fraction → at most 12 integer digits.
    const [whole] = raw.split('.');
    if (whole !== undefined && whole.length > 12) {
      ctx.addIssue({
        code: 'custom',
        message: 'Amount exceeds maximum precision',
      });
      return z.NEVER;
    }
    if (Number.parseFloat(raw) <= 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Amount must be greater than zero',
      });
      return z.NEVER;
    }
    return raw;
  });

/** ISO date (YYYY-MM-DD) — used for `transaction_date`, FY boundaries, etc. */
export const isoDateSchema = z
  .string({ message: 'Date is required' })
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be in YYYY-MM-DD format');

/**
 * UUID — for entity IDs. The default message is intentionally generic
 * because this schema is also used in URL filters and back-end-to-back-end
 * paths where the value is never typed by a human. For user-facing
 * form fields, prefer `labelledUuid(label)` so the error reads like
 * "Please select a fund." instead of UUID jargon.
 */
export const uuidSchema = z.string().uuid('This value is not a valid identifier');

/**
 * UUID schema with a user-language error message keyed off the surface
 * the field represents. The same message is used for missing-value
 * (`undefined`), wrong type (e.g. a number), empty string, and malformed
 * UUID — so the user always sees "Please select a fund." rather than
 * "Invalid input: expected string, received undefined" or the raw UUID
 * regex message.
 */
export function labelledUuid(label: string): z.ZodString {
  const msg = `Please select a ${label}.`;
  return z.string({ message: msg }).uuid(msg);
}

/** Pagination query parameters. */
export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;
